import { ITopicOverviewCard } from "@/api";
import { styles } from "@/config/project";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Sparkles } from "lucide-react";
import { useMemo } from "react";

interface DocumentSummaryHeaderProps {
    headline: string | null;
    topics: ITopicOverviewCard[];
    showGaps?: boolean;
    clausesAnalyzed: number;
    clausesFound: number;
}

export const DocumentSummaryHeader = ({
    headline,
    topics,
    showGaps = true,
    clausesAnalyzed,
    clausesFound,
}: DocumentSummaryHeaderProps) => {
    const theme = useTheme();

    const stats = useMemo(() => {
        const totalTopics = topics.length;
        const totalGaps = clausesAnalyzed - clausesFound;
        return { totalTopics, totalGaps, clausesAnalyzed, clausesFound };
    }, [topics, clausesAnalyzed, clausesFound]);

    return (
        <Box sx={{ mb: 4 }}>
            {/* AI Summary section with headline */}
            {headline && (
                <Box
                    sx={{
                        position: "relative",
                        pl: 2.5,
                        py: 1.5,
                        mb: 3,
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            bgcolor: "primary.main",
                            borderRadius: 1,
                        },
                    }}
                >
                    {/* AI Summary label */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.75}
                        sx={{ mb: 1 }}
                    >
                        <Sparkles
                            size={14}
                            style={{ color: theme.palette.primary.main }}
                        />
                        <Typography
                            variant="overline"
                            sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                letterSpacing: "0.05em",
                            }}
                        >
                            AI Summary
                        </Typography>
                    </Stack>

                    {/* Headline text */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            lineHeight: 1.55,
                            letterSpacing: "-0.01em",
                            maxWidth: 720,
                        }}
                    >
                        {headline}
                    </Typography>
                </Box>
            )}

            {/* Stats section */}
            <Box>
                {/* Main heading: X Topics Analyzed */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        mb: 1,
                    }}
                >
                    {stats.totalTopics} Topics Analyzed
                </Typography>

                {/* Stats badges */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                    useFlexGap
                >
                    {/* Clauses found badge with icon */}
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: "tertiary.main",
                            border: "1px solid",
                            borderColor: theme.palette.grey[300],
                        }}
                    >
                        <CheckCircleOutlineIcon
                            sx={{ fontSize: 16, color: "primary.main" }}
                        />
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {stats.clausesFound} of {stats.clausesAnalyzed}
                        </Typography>
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: "text.secondary" }}
                        >
                            clauses found
                        </Typography>
                    </Box>

                    {/* Not found badge with icon */}
                    {showGaps !== false &&
                        (stats.totalGaps > 0 ? (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 2,
                                    bgcolor: styles.warningLight,
                                    border: "1px solid",
                                    borderColor: styles.warningColor,
                                }}
                            >
                                <ErrorOutlineIcon
                                    sx={{
                                        fontSize: 16,
                                        color: "secondary.main",
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{
                                        fontWeight: 600,
                                        color: "secondary.main",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {stats.totalGaps}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{ color: styles.warningDark }}
                                >
                                    not found
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 2,
                                    bgcolor: styles.successLight,
                                    border: "1px solid",
                                    borderColor: theme.palette.success.light,
                                }}
                            >
                                <CheckCircleOutlineIcon
                                    sx={{ fontSize: 16, color: "success.main" }}
                                />
                                <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{
                                        fontWeight: 600,
                                        color: "success.main",
                                    }}
                                >
                                    All clauses found
                                </Typography>
                            </Box>
                        ))}
                </Stack>
            </Box>
        </Box>
    );
};
